import { enableProdMode } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import * as Sentry from "@sentry/angular";
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { applyAppFont } from './app/core/common/constants/app-fonts.constants';
import { PRE_LOGIN_ENV_FIELDS } from './app/core/common/constants/public-info-fields.constants';

async function fetchPublicInfoBeforeBootstrap() {
	try {
		const fieldsList = [
			'enterpriseInfo.logo',
			'enterpriseInfo._id',
			...PRE_LOGIN_ENV_FIELDS.map(k => `publicInfo.${k}`)
		].join(',');
		const url = (environment.API_URL || '') + `/api/enterprise/get-public-info?fields=${fieldsList}`;

		const res = await fetch(url, { method: 'GET' });
		if (res.ok) {
			const data = await res.json();
			const publicInfo = data?.data?.publicInfo;
			const enterpriseInfo = data?.data?.enterpriseInfo;
			if (publicInfo) {
				try { environment.runtime = {...publicInfo, ...environment.runtime}; } catch (e) { }
			}
			if (enterpriseInfo) {
				try {
					localStorage.setItem('enterprise', JSON.stringify(enterpriseInfo));
					const saved = JSON.parse(localStorage.getItem('layoutConfig')) || {};
					// if no logo is there then update it to empty or ''.
					saved.logo = enterpriseInfo.logo || '';
					localStorage.setItem('layoutConfig', JSON.stringify(saved));
				} catch (e) { }
			}
		}
	} catch (e) {
		// ignore fetch errors and continue bootstrapping
	}
}

if (environment.production) {
	enableProdMode();

	// Keeping it in prod build
	Sentry.init({
		dsn: "https://9b3332f36f244530a630c12def21abe7@o447953.ingest.sentry.io/5428546",
		beforeSend(event, hint) {
			// Drop errors originating from the third-party Heap Analytics script.
			// e.g. "TypeError: r.match is not a function" thrown inside heap.js when its
			// link-click autocapture reads the `target` of an SVG <a> (an SVGAnimatedString,
			// not a string). It is not our code and not actionable on our side.
			const fromHeap = event.exception?.values?.some(value =>
				value.stacktrace?.frames?.some(frame =>
					/heap(js)?[-.]|heap-api/i.test(frame.filename || '')
				)
			);
			if (fromHeap) {
				return null;
			}

			// Drop backend / network error payloads, however they were captured
			// (uncaught, auto-instrumented, or explicit). These are Angular
			// HttpErrorResponses (4xx/5xx) with no useful JS stack — they were the
			// noisy "Non-Error exception captured with keys: error, headers, status,
			// statusText, url" issues. They are backend failures, not frontend defects.
			const original: any = hint?.originalException;
			const looksLikeHttpError =
				original instanceof HttpErrorResponse ||
				(original && typeof original === 'object' &&
					('status' in original || 'statusText' in original) &&
					('url' in original || 'headers' in original));
			if (looksLikeHttpError) {
				return null;
			}

			return event;
		},
	});
}

(async () => {
	await fetchPublicInfoBeforeBootstrap();
	// Apply the tenant-configured font before bootstrap to avoid a flash of the
	// default font. Falls back to the default stack when none is configured.
	applyAppFont(environment.runtime?.BRAND_FONT_FAMILY);
	platformBrowserDynamic()
		.bootstrapModule(AppModule)
		.catch(err => console.log(err));
})();
